import React, {useContext, useState} from 'react';
import { useEffect, useRef } from 'react';
import {composeUrl} from "../../utils/api";
import {AuthContext} from "react-oidc-context";
import {MeasurementResults, HealthRisks, Heartbeat, RisksFactorsScores, CVDiseasesRisks, HardAndFatalEventsRisks, MeasurementPreset, BmiCategory, NAFLDRisk, InitializationResult} from "shenai-sdk";
import {useCspNonce} from "../../utils/useCspNonce";


enum Nafld
{
    Low,
    Moderate,
    High,
}

enum Bmi
{
    UnderweightSevere,
    UnderweightModerate,
    UnderweightMild,
    Normal,
    Overweight,
    ObeseClassI,
    ObeseClassIi,
    ObeseClassIii,
}

export interface Measurements{
    age_years: number;
    average_signal_quality: number;
    bmi_category: string;
    bmi_kg_per_m2: number;
    breathing_rate_bpm: number;
    cardiac_workload_mmhg_per_sec: number;
    diastolic_blood_pressure_mmhg: number;
    heart_rate_bpm: number;
    heartbeats: Heartbeat[];
    height_cm: number;
    hrv_lnrmssd_ms: number;
    hrv_sdnn_ms: number;
    parasympathetic_activity: number;
    stress_index: number;
    systolic_blood_pressure_mmhg: number;
    weight_kg: number
}


interface Risks {
    wellnessScore: number;
    hardAndFatalEvents: HealthRisks["hardAndFatalEvents"] | undefined;
    cvDiseases: HealthRisks["cvDiseases"] | undefined;
    vascularAge: number;
    waistToHeightRatio: number;
    bodyFatPercentage: number;
    basalMetabolicRate: number;
    bodyRoundnessIndex: number;
    conicityIndex: number;
    aBodyShapeIndex: number;
    totalDailyEnergyExpenditure: number;
    scores: HealthRisks["scores"] | undefined;
    hypertensionRisk: number;
    diabetesRisk: number;
    nonAlcoholicFattyLiverDiseaseRisk: string
}

interface HealthAssessmentRequest {
    Status: HealthAssessmentStatus;
    ScanResults: Measurements | null;
    HealthRisks: Risks | null;
}

enum ShenaiEvents {
    StartedMeasurement = 'START_BUTTON_CLICKED',
    StoppedMeasurement = 'STOP_BUTTON_CLICKED',
    CompletedMeasurement = 'MEASUREMENT_FINISHED',
    UserFlowFinished =  'USER_FLOW_FINISHED',
    Error = ''
}

enum HealthAssessmentStatus {
    Started = 'Started',
    Completed = 'Completed',
    Failed = 'Failed',
}

interface ShenaiProps {
    userId: string | null | undefined;
    shenAiToken: string;
}

const getBmiCategoryKey = (value: number) => {
    try {
        const keys = Object.keys(BmiCategory);
        for (const key of keys) {
            const category = BmiCategory[key as keyof typeof BmiCategory];
            if (category && category.value === value) {
                return key;
            }
        }
    } catch (error) {
        console.error('Error getting BMI category key:', error);
    }
    return undefined;
};

const getNafldKey = (value: number) => {
    try {
        const keys = Object.keys(NAFLDRisk);
        for (const key of keys) {
            const risk = NAFLDRisk[key as keyof typeof NAFLDRisk];
            if (risk && risk.value === value) {
                return key;
            }
        }
    } catch (error) {
        console.error('Error getting NAFLD risk key:', error);
    }
    return undefined;
};

const GenerateRequest = function (status: HealthAssessmentStatus, resultsScan: MeasurementResults | null, resultsHealthCheck: HealthRisks | null) {
    switch (status) {
        case HealthAssessmentStatus.Started:
            return {
                Status: HealthAssessmentStatus.Started,
                ScanResults: null,
                HealthRisks: null,
            };
        case HealthAssessmentStatus.Completed:
            const bmiCategoryKey = resultsScan?.bmi_category?.value != null
                ? getBmiCategoryKey(resultsScan.bmi_category.value) ?? ""
                : "";
            const nafldKey = resultsHealthCheck?.nonAlcoholicFattyLiverDiseaseRisk?.value != null
                ? getNafldKey(resultsHealthCheck.nonAlcoholicFattyLiverDiseaseRisk.value) ?? ""
                : "";
            return {
                Status: HealthAssessmentStatus.Completed,
                ScanResults: {
                    age_years: resultsScan?.age_years ?? 0,
                    average_signal_quality: resultsScan?.average_signal_quality ?? 0,
                    bmi_category: bmiCategoryKey ?? "",
                    bmi_kg_per_m2: resultsScan?.bmi_kg_per_m2 ?? 0,
                    breathing_rate_bpm: resultsScan?.breathing_rate_bpm ?? 0,
                    cardiac_workload_mmhg_per_sec: resultsScan?.cardiac_workload_mmhg_per_sec ?? 0,
                    diastolic_blood_pressure_mmhg: resultsScan?.diastolic_blood_pressure_mmhg ?? 0,
                    heart_rate_bpm: resultsScan?.heart_rate_bpm ?? 0,
                    heartbeats: resultsScan?.heartbeats ?? [],
                    height_cm: resultsScan?.height_cm ?? 0,
                    hrv_lnrmssd_ms: resultsScan?.hrv_lnrmssd_ms ?? 0,
                    hrv_sdnn_ms: resultsScan?.hrv_sdnn_ms ?? 0,
                    parasympathetic_activity: resultsScan?.parasympathetic_activity ?? 0,
                    stress_index: resultsScan?.stress_index ?? 0,
                    systolic_blood_pressure_mmhg: resultsScan?.systolic_blood_pressure_mmhg ?? 0,
                    weight_kg: resultsScan?.weight_kg ?? 0
                },
                HealthRisks: {
                    wellnessScore: resultsHealthCheck?.wellnessScore ?? 0,
                    hardAndFatalEvents: resultsHealthCheck?.hardAndFatalEvents,
                    cvDiseases: resultsHealthCheck?.cvDiseases,
                    vascularAge: resultsHealthCheck?.vascularAge ?? 0,
                    waistToHeightRatio: resultsHealthCheck?.waistToHeightRatio ?? 0,
                    bodyFatPercentage: resultsHealthCheck?.bodyFatPercentage ?? 0,
                    basalMetabolicRate: resultsHealthCheck?.basalMetabolicRate ?? 0,
                    bodyRoundnessIndex: resultsHealthCheck?.bodyRoundnessIndex ?? 0,
                    conicityIndex: resultsHealthCheck?.conicityIndex ?? 0,
                    aBodyShapeIndex: resultsHealthCheck?.aBodyShapeIndex ?? 0,
                    totalDailyEnergyExpenditure: resultsHealthCheck?.totalDailyEnergyExpenditure ?? 0,
                    scores: resultsHealthCheck?.scores,
                    hypertensionRisk: resultsHealthCheck?.hypertensionRisk ?? 0,
                    diabetesRisk: resultsHealthCheck?.diabetesRisk ?? 0,
                    nonAlcoholicFattyLiverDiseaseRisk: nafldKey ?? ""
                },
            };
        case HealthAssessmentStatus.Failed:
            return {
                Status: HealthAssessmentStatus.Failed,
                ScanResults: null,
                HealthRisks: null
            };
        default:
            return {
                Status: HealthAssessmentStatus.Failed,
                ScanResults: null,
                HealthRisks: null
            };
    }
};


const CreateSingleScan = async (request: { Status: HealthAssessmentStatus; ScanResults: null; HealthRisks: null } | {
    Status: HealthAssessmentStatus;
    ScanResults: Measurements;
    HealthRisks: Risks;
}, organisationId: number | null, token: string | null) => {
    try {
        const params = new URLSearchParams();
        params.append("organisationId", `${organisationId}`);
        fetch(composeUrl(`v1/healthTracker/Create-Shenai?${params}`), {
            headers: [
                ["Authorization", `Bearer ${token}`],
                ["Content-Type", "application/json"]
            ],
            method: "POST",
            body: JSON.stringify(request)
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
    } catch (error) {
        console.error('There was a problem with the fetch operation:', error);
    }
};

const ShenaiWidget: React.FC<ShenaiProps> = ({userId, shenAiToken}: ShenaiProps)=> {
    
    const auth = useContext(AuthContext);
    const containerRef = useRef<HTMLDivElement>(null);
    // Get CSP nonce for dynamic style creation (if needed for manual style injection)
    // Note: The global patch in App.tsx automatically adds nonce to all style elements created via document.createElement
    const cspNonce = useCspNonce();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const shenaiInstanceRef = useRef<any>(null);
    const tempSdkInstanceRef = useRef<any>(null);
    const isInitializingRef = useRef<boolean>(false);
    const USER_ID = userId ?? '';
    const API_TOKEN = shenAiToken ?? '';

    useEffect(() => {
        // Prevent multiple simultaneous initializations
        if (isInitializingRef.current) {
            return;
        }

        // If SDK is already initialized, don't reinitialize
        if (shenaiInstanceRef.current) {
            return;
        }

        // Ensure canvas exists and is ready
        const canvas = canvasRef.current;
        if (!canvas) {
            console.error('Canvas element not found');
            return;
        }

        // Check if canvas is already transferred (has the Og property set by SDK)
        if ((canvas as any).Og) {
            console.warn('Canvas already transferred, skipping initialization');
            return;
        }

        isInitializingRef.current = true;

        const loadSDK = async () => {
            try {
                const wasmUrl = '/shenai_sdk.wasm';
                console.log(`Fetching .wasm file from URL: ${wasmUrl}`);
                
                const wasmResponse = await fetch(wasmUrl);
                if (!wasmResponse.ok) throw new Error(`Failed to fetch .wasm file: ${wasmResponse.statusText}`);
                
                const wasmBinary = await wasmResponse.arrayBuffer();
                console.log(".wasm file fetched successfully.");
                
                const organisationId: number | null | any = auth?.user?.profile?.lime_organisation_uuid ?? null;
                const token: string | null = auth?.user?.access_token ?? null;
                    
                const CreateShenaiSDK = (await import('../../shenai-sdk')).default;

                const sdkInstance = await CreateShenaiSDK({ wasmBinary } as any);
                
                // Store the instance temporarily so we can clean it up if component unmounts
                // We'll only set shenaiInstanceRef after successful initialization
                tempSdkInstanceRef.current = sdkInstance;
                
                sdkInstance.initialize(
                    API_TOKEN,
                    USER_ID,
                    {
                        hideShenaiLogo: false,
                        measurementPreset: MeasurementPreset.ONE_MINUTE_ALL_METRICS,
                        eventCallback: async (event: any) => {
                            // Check if SDK instance is still valid before using it
                            if (!shenaiInstanceRef.current || shenaiInstanceRef.current !== sdkInstance) {
                                console.warn('SDK instance no longer valid, ignoring event:', event);
                                return;
                            }
                            
                            switch (event) {
                                case ShenaiEvents.StartedMeasurement:
                                    console.log('Received MEASUREMENT_FINISHED');
                                    return;
                                 case ShenaiEvents.StoppedMeasurement:
                                    console.log('Received MEASUREMENT_STOPPED');
                                    return;
                                case ShenaiEvents.CompletedMeasurement:
                                    console.log('Received MEASUREMENT_COMPLETED');
                                    return;
                                case ShenaiEvents.UserFlowFinished:
                                    console.log('Received USER_FLOW_FINISHED');
                                    try {
                                        // Double-check instance is still valid before accessing
                                        if (shenaiInstanceRef.current && shenaiInstanceRef.current === sdkInstance) {
                                            const healthCheckResults = sdkInstance.getHealthRisks();
                                            const scanResults = sdkInstance.getMeasurementResults();
                                            const requestBody =  GenerateRequest(HealthAssessmentStatus.Completed, scanResults, healthCheckResults);
                                            await CreateSingleScan(requestBody, organisationId, token);
                                        } else {
                                            console.warn('SDK instance invalidated during event processing');
                                        }
                                    } catch (error) {
                                        console.error('Error processing USER_FLOW_FINISHED event:', error);
                                    }
                                    return;
                                default:
                                    console.log('Shen.AI event:', event);
                                    return;
                            }
                        },
                        onCameraError: () => console.log('camera error')
                    },
                    (result: InitializationResult) => {
                        isInitializingRef.current = false;
                        
                        // Check if initialization was successful
                        if (result === sdkInstance.InitializationResult.OK) {
                            shenaiInstanceRef.current = sdkInstance;
                            tempSdkInstanceRef.current = null; // Clear temp ref
                            console.log('Shen.AI initialized successfully');
                        } else {
                            console.error('Shen.AI initialization error:', result);
                            
                            // Determine error type
                            let errorMessage = 'Unknown error';
                            if (result === sdkInstance.InitializationResult.INVALID_API_KEY) {
                                errorMessage = 'Invalid API key';
                            } else if (result === sdkInstance.InitializationResult.CONNECTION_ERROR) {
                                errorMessage = 'Connection error';
                            } else if (result === sdkInstance.InitializationResult.INTERNAL_ERROR) {
                                errorMessage = 'Internal error';
                            }
                            console.error(`Shen.AI initialization failed: ${errorMessage}`);
                            
                            // Clean up failed initialization
                            // No need to deinitialize since initialization failed
                            try {
                                sdkInstance.destroyRuntime();
                            } catch (error) {
                                console.error('Error destroying runtime after failed initialization:', error);
                            }
                            shenaiInstanceRef.current = null;
                            tempSdkInstanceRef.current = null;
                        }
                    }
                );
              
            } catch (error) {
                console.error('Failed to initialize Shen.ai SDK:', error);
                isInitializingRef.current = false;
                shenaiInstanceRef.current = null;
            }
        };

        // Small delay to ensure canvas is fully mounted
        const timeoutId = setTimeout(() => {
            loadSDK();
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            
            // If initialization is in progress, mark it as cancelled
            // The initialization callback will handle cleanup if needed
            const wasInitializing = isInitializingRef.current;
            isInitializingRef.current = false;
            
            // Cleanup SDK instance if it exists (either initialized or in progress)
            if (shenaiInstanceRef.current) {
                const instance = shenaiInstanceRef.current;
                try {
                    // First deinitialize to gracefully shut down operations
                    // This stops all active operations before destroying the runtime
                    if (instance.isInitialized && typeof instance.isInitialized === 'function' && instance.isInitialized()) {
                        try {
                            instance.deinitialize();
                        } catch (deinitError) {
                            console.warn('Error during deinitialize (may already be deinitialized):', deinitError);
                        }
                    }
                } catch (error) {
                    console.warn('Error checking initialization status:', error);
                }
                
                // Then destroy the runtime
                try {
                    instance.destroyRuntime();
                } catch (error) {
                    console.error('Error destroying runtime on cleanup:', error);
                }
                shenaiInstanceRef.current = null;
            }
            
            // Also cleanup temporary SDK instance if initialization was in progress
            if (tempSdkInstanceRef.current) {
                try {
                    // For temporary instances, just destroy since they're not fully initialized
                    tempSdkInstanceRef.current.destroyRuntime();
                } catch (error) {
                    console.error('Error destroying temporary SDK instance on cleanup:', error);
                }
                tempSdkInstanceRef.current = null;
            }

            // Reset canvas state if possible
            if (canvas) {
                try {
                    // Remove the Og property if it exists (set by SDK)
                    if ((canvas as any).Og) {
                        (canvas as any).Og = false;
                    }
                } catch (error) {
                    // Ignore errors when resetting canvas state
                }
            }
        };
    }, [API_TOKEN, USER_ID, auth?.user?.profile?.lime_organisation_uuid, auth?.user?.access_token]);

    return (
        <div ref={containerRef} className="wrapper">
            <canvas id="mxcanvas" ref={canvasRef} />
        </div>
    );
}

export default ShenaiWidget;